import React, { useState } from 'react';
import styled from 'astroturf/react';
import type { Props } from './AdminBar';
import { Icon } from 'js/design_system/Icon';
import { isEventsPanelOpen } from './adminEventsPanelStorage';

// Lazy load AdminBar component with code-splitting
const AdminBar = React.lazy(
  () => import(/* webpackChunkName: "admin-bar" */ './AdminBar'),
);

const AdminBarActivator: React.FC<Props> = (props) => {
  const [isActivated, setIsActivated] = useState(() => {
    if (isEventsPanelOpen()) return true;
    return props.openByDefault;
  });

  // Only show for admin users
  if (!props.admin) {
    return null;
  }

  // If activated, render the lazy AdminBar with Suspense
  if (isActivated) {
    return (
      <React.Suspense fallback={<LoadingButton />}>
        <AdminBar {...props} openByDefault={true} />
      </React.Suspense>
    );
  }

  // Otherwise, show activator button
  return <ActivatorButton onClick={() => setIsActivated(true)} />;
};

// Styled components (copied from AdminBar.tsx for consistency)
const ActivatorButtonContainer = styled.button`
  position: fixed;
  width: 64px;
  height: 64px;
  bottom: 14px;
  left: 14px;
  z-index: 2147483000;
  cursor: pointer;
  overflow: hidden;
  color: #fff;
  background-color: #000;
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.33);
  border: none;
  border-radius: 50%;

  display: flex;
  justify-content: center;
  align-items: center;

  opacity: 0.3;
  transition: opacity 0.14s ease-in-out;

  &:hover {
    opacity: 1;
    transition: opacity 0.24s ease-in-out;
  }
`;

const LoadingButtonContainer = styled(ActivatorButtonContainer)`
  cursor: not-allowed;
  background-color: #333;
  opacity: 0.8;

  &:hover {
    opacity: 0.8;
  }
`;

const ActivatorButton: React.FC<React.ComponentPropsWithoutRef<'button'>> = (
  props,
) => (
  <ActivatorButtonContainer {...props}>
    <Icon name="admin_panel_settings" size={24} />
  </ActivatorButtonContainer>
);

const LoadingButton: React.FC = () => (
  <LoadingButtonContainer disabled title="Loading Admin Panel...">
    <Icon name="hourglass_empty" size={24} />
  </LoadingButtonContainer>
);

export default AdminBarActivator;
